import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  AwardBonusListItem,
  AwardBonusListResponse,
  AwardBonusSavePayload,
  AwardBonusVm,
} from '@/api/types/awardBonus.types'
import type { ApiResponse } from '@/api/types'

/**
 * Award Bonus (Payroll) service
 */
export class AwardBonusService {
  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<AwardBonusListResponse> {
    const url = endpoints.awardBonus.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<
      ApiResponse<AwardBonusListResponse> | AwardBonusListResponse | any
    >(url)
    const payload = response.data

    const mapItem = (item: any): AwardBonusListItem => ({
      id: item.id ?? 0,
      employeeId: item.employeeId ?? item.empId ?? item.EmpId ?? item.empid,
      employeeName: item.employeeName ?? item.empName ?? item.employeeNameEn ?? '',
      departmentId: item.departmentId,
      salaryMonthYear: item.salaryMonthYear ?? item.salaryMonth ?? item.monthYear ?? '',
      bonusMonthYear: item.bonusMonthYear ?? item.bonusMonth ?? item.payrollMonthYear ?? item.monthYear ?? '',
      bonusBasedOn: item.bonusBasedOn ?? '',
      debitAccount: item.debitAccount ?? item.debitAccountId,
      debitAccountName: item.debitAccountName ?? '',
      numberOfBonusSalaries: item.numberOfBonusSalaries ?? item.bonusSalaryMonths ?? 0,
      amount: item.amount ?? item.bonusAmount ?? 0,
      details: item.details ?? item.detail ?? '',
      createdDate: item.createdDate ?? item.createdOn ?? '',
    })

    if (payload && typeof payload === 'object') {
      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: payload.data.map(mapItem),
        }
      }
      if (Array.isArray(payload)) {
        return {
          totalRecords: payload.length,
          pageNumber,
          pageSize,
          data: payload.map(mapItem),
        }
      }
    }

    return {
      totalRecords: 0,
      pageNumber,
      pageSize,
      data: [],
    }
  }

  async getById(id: number, branchId: number): Promise<AwardBonusVm> {
    const url = endpoints.awardBonus.getById(id, branchId)
    let response: { data: any }
    try {
      response = await apiClient.get<ApiResponse<AwardBonusVm> | AwardBonusVm | any>(url)
    } catch (err: any) {
      // Fallback: some environments expose getById without branchId in path
      const status = err?.response?.status
      if (status === 404 || status === 400) {
        response = await apiClient.get<ApiResponse<AwardBonusVm> | AwardBonusVm | any>(`/api/AwardBonus/${id}`)
      } else {
        throw err
      }
    }
    let data = response.data
    if (data && 'data' in data && data.data != null) data = data.data
    return {
      id: data?.id ?? id,
      branchId: data?.branchId ?? branchId,
      employeeId: data?.employeeId ?? data?.empId ?? 0,
      departmentId: data?.departmentId ?? 0,
      salaryMonthYear: data?.salaryMonthYear ?? data?.salaryMonth ?? '',
      bonusMonthYear: data?.bonusMonthYear ?? data?.bonusMonth ?? data?.payrollMonthYear ?? '',
      bonusBasedOn: data?.bonusBasedOn ?? 'Amount',
      debitAccount: data?.debitAccount ?? data?.debitAccountId ?? 0,
      numberOfBonusSalaries: data?.numberOfBonusSalaries ?? data?.bonusSalaryMonths ?? 0,
      amount: data?.amount ?? 0,
      details: data?.details ?? '',
    }
  }

  async save(payload: AwardBonusSavePayload): Promise<AwardBonusVm> {
    const url = endpoints.awardBonus.save
    const response = await apiClient.post<ApiResponse<AwardBonusVm> | AwardBonusVm>(url, payload)
    const data = response.data
    return (data && 'data' in data ? (data as ApiResponse<AwardBonusVm>).data : data) as AwardBonusVm
  }

  async update(payload: AwardBonusSavePayload): Promise<AwardBonusVm> {
    const url = endpoints.awardBonus.update
    const response = await apiClient.put<ApiResponse<AwardBonusVm> | AwardBonusVm>(url, payload)
    const data = response.data
    return (data && 'data' in data ? (data as ApiResponse<AwardBonusVm>).data : data) as AwardBonusVm
  }

  async delete(id: number): Promise<void> {
    const url = endpoints.awardBonus.delete(id)
    await apiClient.delete(url)
  }
}

export const awardBonusService = new AwardBonusService()
